/* oxlint-disable no-inner-declarations */
import * as rq from '@tanstack/react-query';

import { useDexFetchClient } from '@/api/looker/utils/use-dex-fetch-client';
import type { CurrentAssetContextValue } from '@/components/current-asset-provider';
import type { Top } from '@/data-explorer/entities/top';
import { useMe } from '@/hooks/use-me';

export namespace ListTops {
  export const key = (input: Input) => [
    'data-explorer',
    'list-tops',
    input.currentAsset?.accureId,
  ];

  export type Input = {
    currentAsset?: CurrentAssetContextValue | null;
  };

  export type QueryParams = {
    retry?: number | boolean;
  };

  export type Output = { tops: Top[] };

  type FetchResult = Record<string, Record<string, Record<string, string>>>;

  function outputFromBody(
    data: FetchResult,
    currentAsset: CurrentAssetContextValue | undefined,
  ): Output {
    const tops: Top[] = [];

    for (const [key, innerObj] of Object.entries(data)) {
      for (const [innerKey, innerValue] of Object.entries(innerObj)) {
        for (const [subKey, id] of Object.entries(innerValue)) {
          tops.push({
            id,
            label: subKey,
            source: key,
            level: innerKey,
          });
        }
      }
    }

    return { tops: tops.filter((top) => filterByAsset(top, currentAsset)) };
  }

  const filterByAsset = (
    top: Top,
    currentAsset: CurrentAssetContextValue | undefined,
  ) => {
    if (currentAsset?.isMobility) {
      return true;
    }

    if (!currentAsset?.asset) {
      return false;
    }

    return (
      top.label.includes(currentAsset.asset.fullFrontendId) ||
      top.label.includes(currentAsset.asset.fullFrontendId.replaceAll(' ', '_'))
    );
  };

  export function useQuery(input: Input, { retry }: QueryParams = {}) {
    const fetchClient = useDexFetchClient();
    const me = useMe();

    return rq.useQuery<Output>({
      retry,
      queryKey: key(input),
      async queryFn({ signal }) {
        const data = await fetchClient<FetchResult>({
          path: `/v1/ids/${me.tenant}`,
          params: {
            'level-type': 'top',
          },
          signal,
        });

        return outputFromBody(data, input.currentAsset ?? undefined);
      },
    });
  }
}
